import { useState } from 'react';

export default function ContactForm({ initialData = null, heading = 'Send en uforpligtende forespørgsel', compact = false }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service_type: initialData?.serviceType || 'privat',
    message: initialData?.notes || '',
    calculator_details: initialData ? JSON.stringify(initialData, null, 2) : '',
    website_url: '', // Mandatory honeypot field
  });

  const [status, setStatus] = useState({ state: 'idle', message: '' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: 'submitting', message: '' });

    // Client-side simple validation
    if (!formData.name.trim() || !formData.phone.trim() || !formData.email.trim() || !formData.message.trim()) {
      setStatus({
        state: 'error',
        message: 'Udfyld venligst alle påkrævede felter (navn, telefon, email og beskrivelse).',
      });
      return;
    }

    try {
      const payload = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        service_type: formData.service_type,
        message: formData.message.trim(),
        website_url: formData.website_url, // Honeypot
      };

      if (formData.calculator_details) {
        payload.calculator_details = formData.calculator_details;
      }

      const res = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setStatus({
          state: 'success',
          message: 'Mange tak for din henvendelse! Vi har modtaget dine oplysninger og vender hurtigt tilbage med et uforpligtende tilbud.',
        });
        setFormData({
          name: '',
          phone: '',
          email: '',
          service_type: 'privat',
          message: '',
          calculator_details: '',
          website_url: '',
        });
      } else {
        const errorData = await res.json().catch(() => null);
        setStatus({
          state: 'error',
          message: errorData?.message || 'Der opstod en fejl ved afsendelse. Ring gerne direkte til os på 31 68 67 64.',
        });
      }
    } catch (err) {
      console.error('Submission error:', err);
      setStatus({
        state: 'error',
        message: 'Kunne ikke oprette forbindelse. Tjek venligst din internetforbindelse eller ring til os på 31 68 67 64.',
      });
    }
  };

  if (status.state === 'success') {
    return (
      <div className="bg-emerald-50 border border-emerald-200 p-8 text-center animate-in fade-in zoom-in duration-300">
        <div className="text-emerald-600 text-3xl mx-auto mb-4">
          <i className="fa-solid fa-circle-check" aria-hidden="true" />
        </div>
        <h3 className="text-xl font-bold text-slate-900 mb-2">Henvendelse modtaget</h3>
        <p className="text-slate-700 leading-relaxed max-w-md mx-auto mb-6">
          {status.message}
        </p>
        <button
          type="button"
          onClick={() => setStatus({ state: 'idle', message: '' })}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
        >
          <span>Send en ny henvendelse</span>
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 sm:p-8 lg:p-10 border border-slate-200/80 shadow-lg shadow-slate-100">
      <div className="mb-6">
        <h3 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
          {heading}
        </h3>
        <p className="text-slate-600 text-sm mt-1">
          Udfyld felterne herunder, så kontakter vi dig hurtigst muligt.
        </p>
      </div>

      {status.state === 'error' && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-3">
          <i className="fa-solid fa-circle-exclamation mt-0.5 text-red-500 shrink-0" aria-hidden="true" />
          <span>{status.message}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Anti-spam Honeypot field (Strictly hidden from humans, visible to bots) */}
        <div className="sr-only absolute -left-[9999px] -top-[9999px]" aria-hidden="true">
          <label htmlFor="website_url">Website URL (leave blank)</label>
          <input
            type="text"
            id="website_url"
            name="website_url"
            value={formData.website_url}
            onChange={handleChange}
            tabIndex={-1}
            autoComplete="off"
          />
        </div>

        {/* Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-1 text-left">
            Dit fulde navn <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            placeholder="F.eks. Mette Hansen"
            className="w-full px-0 py-2.5 bg-transparent border-0 border-b-2 border-slate-200 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-emerald-600 transition-colors text-sm text-left rounded-none"
          />
        </div>

        {/* Phone and Email in 2 cols on tablet/desktop */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 mb-1 text-left">
              Telefonnummer <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              required
              value={formData.phone}
              onChange={handleChange}
              placeholder="F.eks. 31 68 67 64"
              className="w-full px-0 py-2.5 bg-transparent border-0 border-b-2 border-slate-200 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-emerald-600 transition-colors text-sm text-left rounded-none"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-1 text-left">
              Emailadresse <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder="F.eks. din@email.dk"
              className="w-full px-0 py-2.5 bg-transparent border-0 border-b-2 border-slate-200 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-emerald-600 transition-colors text-sm text-left rounded-none"
            />
          </div>
        </div>

        {/* Service Type Selection */}
        <div>
          <label htmlFor="service_type" className="block text-sm font-semibold text-slate-700 mb-1 text-left">
            Hvad drejer henvendelsen sig om?
          </label>
          <select
            id="service_type"
            name="service_type"
            value={formData.service_type}
            onChange={handleChange}
            className="w-full px-0 py-2.5 bg-transparent border-0 border-b-2 border-slate-200 text-slate-900 focus:outline-none focus:border-emerald-600 transition-colors text-sm text-left rounded-none cursor-pointer"
          >
            <option value="privat">Privat rengøring (bolig/lejlighed/villa)</option>
            <option value="erhverv">Erhvervsrengøring (kontor/klinik/butik)</option>
            <option value="hovedrengoering">Hovedrengøring / Grundig rengøring</option>
            <option value="andet">Anden opgave / Generel forespørgsel</option>
          </select>
        </div>

        {/* Message / Description */}
        <div>
          <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-1 text-left">
            Beskrivelse af opgaven <span className="text-red-500">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            required
            rows={compact ? 3 : 4}
            value={formData.message}
            onChange={handleChange}
            placeholder="Fortæl kort om dit behov (f.eks. antal m², ønsket interval eller særlige ønsker)..."
            className="w-full px-0 py-2.5 bg-transparent border-0 border-b-2 border-slate-200 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-emerald-600 transition-colors text-sm text-left rounded-none resize-y"
          />
        </div>

        {/* Submit button */}
        <button
          type="submit"
          disabled={status.state === 'submitting'}
          className="w-full py-3.5 px-6 rounded-full font-bold text-base text-white bg-emerald-600 hover:bg-emerald-700 active:scale-[0.99] transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {status.state === 'submitting' ? (
            <>
              <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
              <span>Sender din besked...</span>
            </>
          ) : (
            <>
              <span>Send henvendelse</span>
              <i className="fa-solid fa-paper-plane text-sm" aria-hidden="true" />
            </>
          )}
        </button>

        <p className="text-xs text-center text-slate-500 pt-1">
          Uforpligtende forespørgsel · Vi svarer hurtigt på hverdage
        </p>
      </form>
    </div>
  );
}
